import { ReactNode, useMemo } from "react";
import { Box, useTheme } from "@mui/material";
import {
  CartesianGrid,
  ComposedChart,
  Customized,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  CatFocus,
  catFocusSx,
  catIdFromEventTarget,
  resolveFocusedCat,
} from "./catFocus";
import {
  ANALYSE_CHART_HEIGHT,
  AXIS_TICK_FONT_SIZE,
  LEAD_TIME_BADGE_HEIGHT,
  LEAD_TIME_X_AXIS_HEIGHT,
  RIGHT_AXIS_CHART_MARGIN,
} from "./constants";
import ActiveCaptions, { Captions } from "./ActiveCaptions";
import LeadTimeAxisBadge from "./LeadTimeAxisBadge";
import { useChartCard } from "./ChartCard";
import { mergeSx } from "../../components/styles";

// Room under the X axis for the hover badge.
const CHART_MARGIN = {
  ...RIGHT_AXIS_CHART_MARGIN,
  bottom: LEAD_TIME_BADGE_HEIGHT,
};

// The <Tooltip> stays for its cursor line (and the syncId link) but shows no
// card: the hovered values read on the active dots (`captions`), the lead
// time / date on the axis badge.
const renderNoTooltip = () => null;

export interface LeadTimeYAxis {
  width: number;
  domain: [number | string, number | string];
  tickFormatter?: (value: number) => string;
  tickCount?: number;
}

export interface FrameFocus {
  focus: CatFocus;
  // Cats the chart renders; the focus only applies to one of them.
  renderedCatIds: number[];
}

/**
 * Axes, grid, hover cursor and lead-time badge shared by the lead-time charts
 * (booking curve, price): X = lead time framed by the shared window, Y on the
 * right. The series are passed as children. Every recharts prop built here is
 * memoized: recharts rebuilds all its series whenever a child prop changes
 * identity, so the frame must not hand it a fresh object per render.
 *
 * With `focus`, the wrapper tracks the pointer to focus a UH cat (see
 * catFocus) — only when several cats are rendered, as a single cat has
 * nothing to fade.
 */
const LeadTimeChartFrame = ({
  chart,
  data,
  date,
  leadTime,
  yAxis,
  focus,
  height = ANALYSE_CHART_HEIGHT,
  syncId = "cockpit-lead-time",
  background,
  captions,
  children,
}: {
  chart: "composed" | "line";
  data: readonly object[];
  // Occupation day (ISO), the badge dates each hovered lead time from it.
  date: string;
  // Left edge of the shared lead-time window (negative; 0 is the right edge).
  leadTime: number;
  // `yAxis` is memoized by the caller (see above).
  yAxis: LeadTimeYAxis;
  focus?: FrameFocus;
  height?: number;
  // Cursor link between charts; null for a chart drawn on its own (a dialog).
  syncId?: string | null;
  // Opaque series (corridor bands) drawn under the hover cursor; `children`
  // are drawn over it so their active dots stay on top.
  background?: ReactNode;
  // Hovered values captioned next to their active dots (memoized by the
  // caller).
  captions?: Captions;
  children: ReactNode;
}) => {
  const theme = useTheme();
  const g = theme.vars.palette.chart;

  // The <Customized> layers are memoized like every other recharts child: a
  // fresh element per render would fail recharts' children check and rebuild
  // the whole chart.
  const captionsLayer = useMemo(
    () =>
      captions ? (
        <Customized component={<ActiveCaptions captions={captions} />} />
      ) : null,
    [captions]
  );
  const badgeLayer = useMemo(
    () => <Customized component={<LeadTimeAxisBadge date={date} />} />,
    [date]
  );

  const xDomain = useMemo<[number, number]>(() => [leadTime, 0], [leadTime]);
  // Explicit ticks anchored on 0 (the occupation day): recharts' automatic
  // ticks align on the domain start instead, leaving an odd gap before 0.
  const xTicks = useMemo(() => {
    const span = -leadTime;
    const step =
      [5, 10, 15, 20, 25, 30, 50].find((s) => span / s <= 10) ??
      Math.ceil(span / 10);
    const ticks: number[] = [];
    for (let value = 0; value >= leadTime; value -= step) ticks.push(value);
    return ticks;
  }, [leadTime]);
  const tick = useMemo(
    () => ({ fontSize: AXIS_TICK_FONT_SIZE, fill: g.axis }),
    [g.axis]
  );
  const axisLine = useMemo(() => ({ stroke: g.grid }), [g]);

  const focusedCatId = focus
    ? resolveFocusedCat(focus.focus.pointerCatId, focus.renderedCatIds)
    : null;
  const trackPointer = focus != null && focus.renderedCatIds.length > 1;

  const card = useChartCard();
  // Fullscreen square: the side follows the width, capped by the viewport.
  const square = card.inModal && card.format === "square";

  const Chart = chart === "line" ? LineChart : ComposedChart;
  // Shared syncId: hovering one lead-time chart moves the cursor on the other
  const chartElement = (
    <Chart
      data={data as object[]}
      margin={CHART_MARGIN}
      syncId={syncId ?? undefined}
      syncMethod="value"
    >
      <CartesianGrid stroke={g.grid} vertical={false} />
      <XAxis
        dataKey="leadTime"
        type="number"
        height={LEAD_TIME_X_AXIS_HEIGHT}
        domain={xDomain}
        allowDataOverflow
        ticks={xTicks}
        tick={tick}
        tickLine={false}
        axisLine={axisLine}
      />
      <YAxis
        orientation="right"
        width={yAxis.width}
        domain={yAxis.domain}
        tickCount={yAxis.tickCount ?? 6}
        tick={tick}
        tickLine={false}
        axisLine={false}
        allowDecimals={false}
        tickFormatter={yAxis.tickFormatter}
      />
      {background}
      <Tooltip content={renderNoTooltip} />
      {children}
      {captionsLayer}
      {badgeLayer}
    </Chart>
  );

  return (
    <Box
      sx={[
        ...mergeSx(catFocusSx(focusedCatId)),
        square && { width: "min(100%, calc(100vh - 200px))", mx: "auto" },
      ]}
      onMouseMove={
        trackPointer
          ? (e) => focus.focus.setPointerCatId(catIdFromEventTarget(e.target))
          : undefined
      }
      onMouseLeave={
        trackPointer ? () => focus.focus.setPointerCatId(null) : undefined
      }
    >
      {square ? (
        <ResponsiveContainer width="100%" aspect={1}>
          {chartElement}
        </ResponsiveContainer>
      ) : (
        <ResponsiveContainer width="100%" height={height}>
          {chartElement}
        </ResponsiveContainer>
      )}
    </Box>
  );
};

export default LeadTimeChartFrame;
